import {
  type CellContext,
  type ColumnFiltersState,
  createColumnHelper,
  type PaginationState,
  type VisibilityState,
} from '@tanstack/react-table';
import { Loader2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useGetTrialConversion } from '~entities/dashboard/api/get-trial-conversion';
import {
  type TrialStudent,
  useGetAllTrialStudents,
} from '~entities/student/api/get-all-trial-students';
import { useCurrentStudio } from '~entities/studios/model/studio-store';

import { getTrialMonthDateRange } from '~features/new-dashboard/helpers/trial-month-date-range';
import { TrialConversionSummary } from '~features/new-dashboard/ui/trial-conversion-summary';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import {
  formatTrialDateDisplay,
  formatTrialTimeDisplay,
} from '~shared/helpers/format-trial-datetime';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { Badge } from '~shared/shadcn/ui/badge';

const columnHelper = createColumnHelper<TrialStudent>();

const getStatusVariant = (
  status: TrialStudent['appointmentStatus'],
): 'secondary' | 'default' | 'destructive' => {
  if (status === 'Trial Scheduled') return 'secondary';
  if (status === 'Trial Completed') return 'default';
  return 'destructive';
};

interface TrialStudentCardProps {
  trial: TrialStudent;
  studioTimezone?: string;
}

const TrialStudentCard = ({ trial, studioTimezone }: TrialStudentCardProps) => {
  const classTitle = (trial.classId as TrialStudent['classId'] | undefined)
    ?.title;

  return (
    <div className='rounded-xl border border-border/50 p-3.5 transition-all duration-150 hover:bg-muted/40'>
      <div className='mb-2 flex items-start justify-between gap-2'>
        <div className='min-w-0'>
          <p className='truncate text-sm font-semibold text-[#000814]'>
            {trial.studentName}
          </p>
          <p className='truncate text-xs text-muted-foreground'>
            {classTitle ?? '—'}
          </p>
        </div>
        <Badge
          variant={getStatusVariant(trial.appointmentStatus)}
          className='shrink-0 text-[10px]'
        >
          {trial.appointmentStatus}
        </Badge>
      </div>
      <div className='space-y-1 text-xs text-muted-foreground'>
        <p className='truncate'>
          <span className='font-medium text-foreground'>Parent:</span>{' '}
          {trial.name}
        </p>
        <p className='truncate'>
          <span className='font-medium text-foreground'>Phone:</span>{' '}
          {trial.contactNo || '—'}
        </p>
        <p className='truncate'>
          <span className='font-medium text-foreground'>Email:</span>{' '}
          {trial.email || '—'}
        </p>
        <p>
          <span className='font-medium text-foreground'>Trial:</span>{' '}
          {formatTrialDateDisplay(trial.startTime, studioTimezone)} ·{' '}
          {formatTrialTimeDisplay(trial.startTime, studioTimezone)} –{' '}
          {formatTrialTimeDisplay(trial.endTime, studioTimezone)}
        </p>
      </div>
    </div>
  );
};

export const TrialsTab = () => {
  const studioTimezone = useCurrentStudio()?.timezone;
  const {
    startDate: formattedFirstDayOfMonth,
    endDate: formattedLastDayOfMonth,
  } = getTrialMonthDateRange();

  const cols = useMemo(
    () => [
      columnHelper.accessor('classId.title', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4'
            column={info.column}
            title='Class Name'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return <div className='px-4'>{value}</div>;
        },
      }),
      columnHelper.accessor('name', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4'
            column={info.column}
            title='Parent Name'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return <div className='px-4'>{value}</div>;
        },
      }),
      columnHelper.accessor('contactNo', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4'
            column={info.column}
            title='Parent Phone'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return <div className='px-4'>{value}</div>;
        },
      }),
      columnHelper.accessor('email', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4'
            column={info.column}
            title='Parent Email'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return <div className='flex items-center gap-2 px-4'>{value}</div>;
        },
      }),
      columnHelper.accessor('studentName', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4'
            column={info.column}
            title='Student Name'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return <div className='px-4'>{value}</div>;
        },
      }),
      columnHelper.accessor('startTime', {
        id: 'trialDate',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4'
            column={info.column}
            title='Trial Date'
            disableHiding
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className='px-4'>
              {formatTrialDateDisplay(value, studioTimezone)}
            </div>
          );
        },
      }),
      columnHelper.accessor('startTime', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4'
            column={info.column}
            title='Start Time'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className='px-4'>
              {formatTrialTimeDisplay(value, studioTimezone)}
            </div>
          );
        },
      }),
      columnHelper.accessor('endTime', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4'
            column={info.column}
            title='End Time'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className='px-4'>
              {formatTrialTimeDisplay(value, studioTimezone)}
            </div>
          );
        },
      }),
      columnHelper.accessor('appointmentStatus', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4'
            column={info.column}
            title='Status'
            disableSorting
          />
        ),
        cell: (
          info: CellContext<
            TrialStudent,
            | 'Trial Scheduled'
            | 'Trial Completed'
            | 'Trial Missed'
            | 'Enrolled'
            | 'Not Enrolled'
          >,
        ) => {
          return (
            <Badge variant={getStatusVariant(info.getValue())}>
              {info.getValue()}
            </Badge>
          );
        },
      }),
    ],
    [studioTimezone],
  );

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const { data: trialStudents, isFetching } = useGetAllTrialStudents({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    startDate: formattedFirstDayOfMonth,
    endDate: formattedLastDayOfMonth,
    dateField: 'startTime',
  });

  const { data: trialConversion, isLoading: isLoadingConversion } =
    useGetTrialConversion({
      startDate: formattedFirstDayOfMonth,
      endDate: formattedLastDayOfMonth,
      dateField: 'startTime',
    });

  const trials = trialStudents?.data ?? [];
  const totalCount = trialStudents?.pagination.totalCount ?? 0;

  return (
    <div className='w-full min-w-0'>
      <TrialConversionSummary
        metrics={trialConversion}
        isLoading={isLoadingConversion}
        variant='inline'
        label='Trial conversion this month'
        className='mb-3 text-xs md:text-sm'
      />

      <EnrollioTable
        ariaLabel='Trial students requiring attention'
        totalLabel='Trial students'
        filterHeader={(table) => (
          <EnrollioTableSearch
            value={
              (table.getColumn('studentName')?.getFilterValue() as
                | string
                | undefined) ?? ''
            }
            onChange={(value) => {
              table.getColumn('studentName')?.setFilterValue(value);
            }}
            placeholder='Search student name...'
          />
        )}
        toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
        columnFilters={columnFilters}
        columnVisibility={columnVisibility}
        columns={cols}
        data={trials}
        rowCount={totalCount}
        enableRowSelection={true}
        isUpdating={isFetching}
        onColumnFiltersChange={setColumnFilters}
        onColumnVisibilityChange={setColumnVisibility}
        pagination={pagination}
        onPaginationChange={setPagination}
        manualPagination
        mobile={() => (
          <div className='space-y-3'>
            {isFetching ? (
              <div className='flex items-center justify-center py-8'>
                <Loader2Icon className='size-8 animate-spin' />
              </div>
            ) : trials.length > 0 ? (
              <div className='space-y-2'>
                {trials.map((trial) => (
                  <TrialStudentCard
                    key={trial._id}
                    trial={trial}
                    studioTimezone={studioTimezone}
                  />
                ))}
              </div>
            ) : (
              <div className='py-8 text-center text-sm text-muted-foreground'>
                No trial students found this month
              </div>
            )}
          </div>
        )}
      />
    </div>
  );
};
